import { readableText } from "@pax/api-contract/ai/shared";
import { focusHalo, focusHaloFlush, panelMotion, press } from "@pax/ui";
import { Archive, ArchiveRestore, Check, ChevronDown, Download, Menu, Plus } from "lucide-react";
import { type ReactNode, useState } from "react";
import { BackChevron } from "@/components/BackChevron";
import { markButton } from "./Bubbles";

export const field = `w-full rounded-xl bg-foreground/5 px-3 py-2 text-sm outline-none focus:bg-foreground/[0.07] ${panelMotion}`;
export const areaField = `mt-1.5 w-full resize-none rounded-2xl bg-foreground/5 px-4 py-3 text-sm leading-6 outline-none [field-sizing:content] focus:bg-foreground/[0.07] ${panelMotion}`;
export const titleField = `-mx-2 block w-[calc(100%+1rem)] rounded-xl bg-transparent px-2 py-0.5 text-2xl font-semibold tracking-tight outline-none placeholder:text-muted-foreground/40 hover:bg-foreground/5 focus:bg-foreground/5 ${panelMotion}`;
export const descriptionField = `-mx-2 mt-1 block w-[calc(100%+1rem)] resize-none rounded-xl bg-transparent px-2 py-1 text-sm leading-6 text-muted-foreground outline-none [field-sizing:content] placeholder:text-muted-foreground/40 hover:bg-foreground/5 focus:bg-foreground/5 ${panelMotion}`;
export const fieldLabel = "text-xs text-muted-foreground";
export const link = `rounded-md underline-offset-4 decoration-foreground/20 hover:text-foreground hover:underline ${focusHaloFlush}`;

export const button = `rounded-full bg-foreground/5 px-4 py-2 text-sm hover:bg-foreground/10 disabled:opacity-40 ${focusHaloFlush} ${panelMotion}`;
export const primaryButton = `h-9 rounded-full bg-foreground px-4 text-sm font-medium text-background hover:bg-foreground/85 disabled:opacity-40 ${panelMotion} ${press} ${focusHalo}`;
export const ghostButton = `h-9 rounded-full px-4 text-sm font-medium text-muted-foreground hover:bg-foreground/5 hover:text-foreground disabled:opacity-40 ${panelMotion} ${press} ${focusHalo}`;

const hiddenCorner =
  "opacity-40 transition-opacity sm:opacity-0 hover:opacity-100 focus-within:opacity-100 has-[[data-open]]:opacity-100";

/**
 * A page: the way back in the screen's top-left corner, downloads and `actions` in its top-right,
 * and an optional `hero` running the page's full width above its content.
 */
export function Workspace({
  children,
  back,
  download,
  downloadMenu,
  actions,
  hero,
}: {
  children: ReactNode;
  back?: () => void;
  /** The one thing this page has to take away. */
  download?: { label: string; save: () => void };
  /** A choice of things to take away, for pages with more than one. */
  downloadMenu?: ReactNode;
  actions?: ReactNode;
  hero?: ReactNode;
}) {
  return (
    <div className="relative min-h-full">
      {back && <BackChevron onBack={back} className="absolute top-4 left-4 z-30" />}
      <div className="absolute top-4 right-4 z-30 flex items-center gap-2">
        {downloadMenu && <div className={hiddenCorner}>{downloadMenu}</div>}
        {download && <SaveButton {...download} />}
        {actions}
      </div>
      {hero}
      <div
        className={`relative mx-auto max-w-3xl space-y-8 px-6 pb-16 sm:px-12 ${hero ? "pt-8" : "pt-16 sm:pt-20"}`}
      >
        {children}
      </div>
    </div>
  );
}
function SaveButton({ label, save }: { label: string; save: () => void }) {
  const [saved, setSaved] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        save();
        setSaved(true);
        setTimeout(() => setSaved(false), 1500);
      }}
      aria-label={label}
      title={label}
      className={`${markButton} ${saved ? "opacity-100" : hiddenCorner}`}
    >
      {saved ? <Check className="size-4" /> : <Download className="size-4" />}
    </button>
  );
}
export function Row({
  title,
  description,
  score,
  onClick,
}: {
  title: string;
  description?: string;
  score?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mx-4 flex w-[calc(100%+2rem)] items-center gap-6 rounded-2xl px-4 py-3 text-left hover:bg-foreground/5 ${focusHaloFlush} ${panelMotion}`}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        {description && (
          <span className="mt-0.5 block truncate text-xs leading-5 text-muted-foreground">
            {description}
          </span>
        )}
      </span>
      {score && <span className="text-sm tabular-nums">{score}</span>}
    </button>
  );
}
export function AddRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mx-4 flex w-[calc(100%+2rem)] items-center gap-2 rounded-2xl px-4 py-3 text-left text-sm text-muted-foreground hover:bg-foreground/5 hover:text-foreground ${panelMotion} ${focusHaloFlush}`}
    >
      <Plus className="size-4" />
      {label}
    </button>
  );
}
/** The foot of a list: what has been archived, folded away until asked for. */
export function ArchivedRows({
  children,
  count,
  label = "Archived",
}: {
  children: ReactNode;
  count: number;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  if (count === 0) return null;
  return (
    <div className="mt-6">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={`-ml-3 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs text-muted-foreground hover:bg-foreground/5 hover:text-foreground ${panelMotion} ${focusHaloFlush}`}
      >
        {label} · {count}
        <ChevronDown className={`size-3.5 ${panelMotion} ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-1 opacity-70">{children}</div>}
    </div>
  );
}
/** The foot of a page: taking it out of the lists, or putting it back. */
export function ArchiveButton({
  noun,
  archived,
  onChange,
}: {
  noun: string;
  archived: boolean;
  onChange: (archived: boolean) => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  return (
    <div className="border-t border-foreground/10 pt-6">
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(undefined);
          try {
            await onChange(!archived);
          } catch (error) {
            setError(error);
          } finally {
            setBusy(false);
          }
        }}
        className={`${ghostButton} -ml-4 inline-flex items-center gap-2`}
      >
        {archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
        {archived ? `Restore this ${noun}` : `Archive this ${noun}`}
      </button>
      <Failure error={error} />
    </div>
  );
}
export function Section({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <section>
      {label && <p className="mb-1 text-xs text-muted-foreground">{label}</p>}
      {children}
    </section>
  );
}
export function Heading({
  above,
  title,
  description,
  action,
  children,
}: {
  above?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  /** Short facts under the description. */
  children?: ReactNode;
}) {
  return (
    <div>
      {above && (
        <div className="-ml-3 mb-1 flex items-center gap-2 text-sm text-muted-foreground">
          {above}
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {description && (
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
          )}
        </div>
        {action}
      </div>
      {children && (
        <div className="mt-3 space-y-0.5 text-xs leading-5 text-muted-foreground">{children}</div>
      )}
    </div>
  );
}
export function NewButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`grid size-9 shrink-0 place-items-center rounded-full bg-foreground/5 hover:bg-foreground/10 ${panelMotion} ${press} ${focusHaloFlush}`}
    >
      <Plus className="size-4" />
    </button>
  );
}
export function Tabs<T extends string>({
  value,
  options,
  onChange,
  compact = false,
}: {
  value: T;
  /** `count` follows the label in a quieter tone, such as how many rows the tab lists. */
  options: { value: T; label: string; count?: number }[];
  onChange: (value: T) => void;
  compact?: boolean;
}) {
  return (
    <div
      className={`inline-flex max-w-full overflow-x-auto rounded-full bg-foreground/5 ${compact ? "p-0.5" : "p-1"}`}
    >
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={`shrink-0 rounded-full whitespace-nowrap ${compact ? "px-2.5 py-0.5 text-xs" : "px-3 py-1.5 text-sm"} ${value === option.value ? "bg-background font-medium shadow-sm" : "text-muted-foreground"}`}
        >
          {option.label}
          {option.count !== undefined && (
            <span className="ml-1.5 font-normal text-muted-foreground tabular-nums">
              {option.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
/** A line that opens to show more. The content is only drawn while open. */
export function Disclosure({
  summary,
  children,
  initiallyOpen = false,
  open: givenOpen,
  onOpenChange,
  stacked = false,
  className = "",
}: {
  summary: ReactNode;
  children: () => ReactNode;
  initiallyOpen?: boolean;
  /** Whether it is open, for a caller that keeps it; the disclosure keeps it otherwise. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** A row standing for several folded together: a faint tint, and a hamburger icon while closed. */
  stacked?: boolean;
  className?: string;
}) {
  const [ownOpen, setOwnOpen] = useState(initiallyOpen);
  const open = givenOpen ?? ownOpen;
  const setOpen = onOpenChange ?? setOwnOpen;
  return (
    <div className={className}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={`-mx-2 flex w-[calc(100%+1rem)] items-center gap-2 rounded-xl px-2 py-1.5 text-left text-sm ${stacked ? "bg-foreground/[0.06] hover:bg-foreground/[0.09]" : "hover:bg-foreground/5"} ${focusHaloFlush} ${panelMotion}`}
      >
        {stacked && !open ? (
          <Menu className="size-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown
            className={`size-3.5 shrink-0 text-muted-foreground ${panelMotion} ${open ? "" : "-rotate-90"}`}
          />
        )}
        <span className="min-w-0 flex-1">{summary}</span>
      </button>
      {open && <div className="mt-1 mb-2 pl-5.5">{children()}</div>}
    </div>
  );
}

/** A value laid out to read, with a switch to the exact text it was recorded as. */
export function ReadableValue({ value }: { value: unknown }) {
  const [showRaw, setShowRaw] = useState(false);
  const raw = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  const readable = readableText(value);
  return (
    <div className="group relative">
      <pre className="max-h-[32rem] overflow-auto rounded-xl bg-foreground/5 p-3 font-mono text-xs leading-5 break-words whitespace-pre-wrap">
        {showRaw ? raw : readable}
      </pre>
      {readable !== raw && (
        <button
          type="button"
          onClick={() => setShowRaw(!showRaw)}
          className={`absolute top-2 right-4 rounded-full bg-background/90 px-2 py-0.5 text-xs text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground focus-visible:opacity-100 ${focusHaloFlush} ${panelMotion}`}
        >
          {showRaw ? "Readable" : "Raw"}
        </button>
      )}
    </div>
  );
}

export function Failure({ error }: { error: unknown }) {
  return error ? (
    <p role="alert" className="text-sm text-destructive">
      {error instanceof Error ? error.message : String(error)}
    </p>
  ) : null;
}
export function relativeTime(date: string) {
  const elapsed = Math.max(0, Date.now() - new Date(date).valueOf());
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  return elapsed < 60000
    ? "just now"
    : elapsed < 3600000
      ? formatter.format(-Math.floor(elapsed / 60000), "minute")
      : elapsed < 86400000
        ? formatter.format(-Math.floor(elapsed / 3600000), "hour")
        : formatter.format(-Math.floor(elapsed / 86400000), "day");
}
export function Moment({ date }: { date: string }) {
  const [exact, setExact] = useState(false);
  const label = relativeTime(date);
  return (
    <button
      type="button"
      className="text-xs text-muted-foreground"
      onClick={() => setExact(!exact)}
      title={new Date(date).toLocaleString()}
    >
      {exact ? new Date(date).toLocaleString() : label}
    </button>
  );
}
export function downloadJson(name: string, value: unknown) {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(value, null, 2)], { type: "application/json" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = `${name}.json`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const plural = (count: number, noun: string) =>
  `${count} ${count === 1 ? noun : `${noun}s`}`;

/** Who plays a case: "Kessa Vorn", or "Harriet Cole and Marco Ruiz". */
function castOf(players: readonly { name: string }[]) {
  const names = players.map(player => player.name);
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : names[0];
}

export const caseSummary = (item: { players: readonly { name: string }[]; rounds: number }) =>
  [castOf(item.players), `${item.rounds} rounds`].filter(Boolean).join(" · ");

export const formatUsd = (value: number) =>
  value < 0.01 && value > 0 ? `$${value.toFixed(4)}` : `$${value.toFixed(value < 1 ? 3 : 2)}`;

/** A span of time in its two largest units: `840ms`, `3.4s`, `2m 5s`, `3h 12m`, `4d 6h`. */
export function formatDuration(milliseconds: number) {
  if (milliseconds < 1000) return `${Math.round(milliseconds)}ms`;
  if (milliseconds < 60_000) return `${(milliseconds / 1000).toFixed(1)}s`;
  const seconds = Math.round(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m ${seconds % 60}s`;
}

export const formatCount = (value: number) =>
  new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(value);

/** A quiet color for a score out of 100: green when good, amber when middling, red when poor. */
export function scoreTone(score: number | null | undefined) {
  if (score === null || score === undefined) return "bg-foreground/5 text-muted-foreground";
  if (score >= 75) return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
  if (score >= 50) return "bg-amber-500/15 text-amber-700 dark:text-amber-300";
  return "bg-rose-500/15 text-rose-700 dark:text-rose-300";
}

export function scoreLabel(points: { score: number | null; completed: number }[]) {
  const measured = points.filter(point => point.score !== null && point.completed > 0);
  const count = measured.reduce((sum, point) => sum + point.completed, 0);
  return count
    ? `${(measured.reduce((sum, point) => sum + (point.score ?? 0) * point.completed, 0) / count).toFixed(0)}%`
    : "—";
}
